import { useEffect, useRef } from 'react';

interface MatchSummaryHeaderProps {
  totalMatches: number;
  totalQuestions?: number;
}

type ScoreBand = {
  title: string;
  description: string;
};

const SCORE_BANDS: Array<{ max: number; copy: ScoreBand }> = [
  {
    max: 19,
    copy: {
      title: 'Curious Beginnings',
      description: "Every shared desire is a real spark. You've found a starting point to build on together.",
    },
  },
  {
    max: 39,
    copy: {
      title: 'Playful Explorers',
      description: "You've already got a meaningful spark, with plenty here to enjoy and more to discover together.",
    },
  },
  {
    max: 59,
    copy: {
      title: 'Spicy Adventurers',
      description: "You have a strong overlap and lots of room to play. You're clearly open to exploring together.",
    },
  },
  {
    max: 79,
    copy: {
      title: 'Wild & Adventurous',
      description: 'You share an exciting amount of overlap, with curiosity, chemistry, and momentum on your side.',
    },
  },
  {
    max: 100,
    copy: {
      title: 'Absolutely Electric',
      description: 'Your desires line up in a big way. That gives you a lot to enjoy and even more to explore together.',
    },
  },
];

function getScoreBand(score: number): ScoreBand {
  const fallback = SCORE_BANDS[SCORE_BANDS.length - 1];
  if (!fallback) {
    throw new Error('Missing score band configuration.');
  }
  return SCORE_BANDS.find((band) => score <= band.max)?.copy ?? fallback.copy;
}

export default function MatchSummaryHeader({
  totalMatches,
  totalQuestions,
}: MatchSummaryHeaderProps) {
  const confettiFired = useRef(false);

  useEffect(() => {
    if (confettiFired.current || totalMatches === 0) return;
    confettiFired.current = true;

    // Dynamic import for confetti — gracefully skip if not installed
    import('canvas-confetti')
      .then((confettiModule) => {
        const confetti = confettiModule.default;
        confetti({
          particleCount: 120,
          spread: 80,
          origin: { y: 0.3 },
          colors: ['#FF3366', '#FF8C42', '#fab1a0', '#ffeaa7', '#74b9ff'],
        });
      })
      .catch(() => {
        // canvas-confetti not available — skip silently
      });
  }, [totalMatches]);

  const showPercentage = totalQuestions != null && totalQuestions > 0;
  const percentage = showPercentage
    ? Math.round((totalMatches / totalQuestions) * 100)
    : null;
  const score = percentage ?? 0;
  const band = getScoreBand(score);

  return (
    <div className="text-center space-y-4 sm:space-y-5">
      <div className="flex justify-center">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
          Shared {totalMatches === 1 ? 'Desire' : 'Desires'}
        </span>
      </div>

      <div className="space-y-3">
        <div className="text-7xl font-black tracking-[-0.05em] text-primary drop-shadow-[0_0_24px_rgba(255,51,102,0.38)] sm:text-8xl">
          {totalMatches}
        </div>
        <h1 className="text-gradient text-3xl font-extrabold uppercase tracking-[-0.03em] sm:text-5xl">
          {band.title}
        </h1>
      </div>

      <p className="mx-auto max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
        {band.description}
      </p>
    </div>
  );
}
